import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BM25 } from '../utils/bm25';

interface Calculator {
  id: string;
  title: string;
  path: string;
  category: string;
  content: string;
}

const calculators: Calculator[] = [
  // Hull and Construction
  {
    id: 'hull-volume',
    title: 'Hull Volume Calculator',
    path: '/hull-volume',
    category: 'Hull and Construction',
    content: 'Calculate boat hull volume displacement marine vessel construction'
  },
  {
    id: 'inflatable-volume',
    title: 'Inflatable Volume Calculator',
    path: '/inflatable-volume',
    category: 'Hull and Construction',
    content: 'Calculate inflatable boat tube volume air capacity chambers'
  },
  // ... existing calculators ...

  // Electric Motors
  {
    id: 'electric-motor-power-thrust',
    title: 'Electric Motor Power-to-Thrust Calculator',
    path: '/electric-motor-power-thrust',
    category: 'Electric Motors',
    content: 'Calculate electric motor power thrust output conversion efficiency marine propulsion'
  },
  {
    id: 'electric-motor-efficiency',
    title: 'Electric Motor Efficiency Calculator',
    path: '/electric-motor-efficiency',
    category: 'Electric Motors',
    content: 'Calculate electric motor efficiency power loss temperature performance'
  },
  {
    id: 'electric-motor-rpm-speed',
    title: 'Electric Motor RPM to Speed Calculator',
    path: '/electric-motor-rpm-speed',
    category: 'Electric Motors',
    content: 'Convert electric motor RPM boat speed propeller gear ratio'
  },
  {
    id: 'torque-output',
    title: 'Torque Output Calculator',
    path: '/torque-output',
    category: 'Electric Motors',
    content: 'Calculate electric motor torque output power RPM efficiency'
  },
  {
    id: 'voltage-to-speed',
    title: 'Voltage to Speed Calculator',
    path: '/voltage-to-speed',
    category: 'Electric Motors',
    content: 'Convert voltage input to motor speed RPM performance'
  },
  {
    id: 'wattage-requirement',
    title: 'Motor Wattage Calculator',
    path: '/wattage-requirement',
    category: 'Electric Motors',
    content: 'Calculate required motor power wattage thrust performance'
  },
  {
    id: 'propeller-efficiency',
    title: 'Propeller Efficiency Calculator',
    path: '/propeller-efficiency',
    category: 'Electric Motors',
    content: 'Calculate propeller efficiency motor matching performance'
  },
  {
    id: 'current-draw',
    title: 'Current Draw Calculator',
    path: '/current-draw',
    category: 'Electric Motors',
    content: 'Calculate motor current draw amperage wire sizing'
  },
  {
    id: 'heat-dissipation',
    title: 'Heat Dissipation Calculator',
    path: '/heat-dissipation',
    category: 'Electric Motors',
    content: 'Calculate motor heat generation cooling requirements temperature'
  },
  {
    id: 'controller-efficiency',
    title: 'Controller Efficiency Calculator',
    path: '/controller-efficiency',
    category: 'Electric Motors',
    content: 'Calculate motor controller efficiency power loss performance'
  }
];

const searchEngine = new BM25(calculators);

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Calculator[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    if (searchQuery.trim()) {
      const searchResults = searchEngine.search(searchQuery);
      setResults(searchResults);
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (path: string) => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    navigate(path);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search calculators..."
          className="w-64 pl-10 pr-8 py-2 rounded-md bg-blue-700 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <Search className="absolute left-3 top-2.5 h-5 w-5 text-blue-200" />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-2.5 text-blue-200 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-96 mt-2 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            {results.map((result) => (
              <button
                key={result.id}
                onClick={() => handleSelect(result.path)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                <div className="text-sm font-medium text-gray-900">{result.title}</div>
                <div className="text-xs text-gray-500">{result.category}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;